import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Toolbar,
  Typography,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowBack,
  ArrowForward,
  Delete as DeleteIcon,
  FilterListOff,
  Print,
  Refresh,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { TableFilterCell } from '../components/TableFilterCell';
import { statusLabel } from '../i18n/translate';
import { employeeApi } from '../services/api';
import { DETAIL_FORM_UNIFORM_FIELDS_SX } from '../styles/detailFormStyles';
import { Employee, EmployeeCreateRequest, EmployeeStatus, EmployeeUpdateRequest } from '../types/employee';

type EmployeeFormState = EmployeeCreateRequest & { status: EmployeeStatus };

const DEFAULT_FORM: EmployeeFormState = {
  employeeCode: '',
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: '',
  jobTitle: '',
  notes: '',
  status: 'ACTIVE',
};

const PAGE_SIZE = 8;

function getStatusColor(status: EmployeeStatus) {
  return status === 'ACTIVE' ? 'success' : 'default';
}

export const EmployeeManagementPage: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [formData, setFormData] = useState<EmployeeFormState>(DEFAULT_FORM);
  const [isCreating, setIsCreating] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
  const [filters, setFilters] = useState({
    firstName: '',
    lastName: '',
    employeeCode: '',
    email: '',
    phoneNumber: '',
    jobTitle: '',
    status: '',
    linkedUser: '',
  });

  const { data: employees = [], isLoading } = useQuery('employees', employeeApi.getAll);

  const resetForm = () => {
    setFormData(DEFAULT_FORM);
    setSelectedEmployee(null);
    setIsCreating(false);
    setIsDirty(false);
  };

  const invalidateEmployeeQueries = () => {
    queryClient.invalidateQueries('employees');
    queryClient.invalidateQueries('availableEmployees');
    queryClient.invalidateQueries('expenses');
  };

  const createMutation = useMutation(employeeApi.create, {
    onSuccess: () => {
      invalidateEmployeeQueries();
      toast.success(t('employeeRecords.messages.created', 'Employee created successfully'));
      resetForm();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || t('employeeRecords.messages.createFailed', 'Failed to create employee'));
    },
  });

  const updateMutation = useMutation(
    ({ id, data }: { id: string; data: EmployeeUpdateRequest }) => employeeApi.update(id, data),
    {
      onSuccess: (employee) => {
        invalidateEmployeeQueries();
        toast.success(t('employeeRecords.messages.updated', 'Employee updated successfully'));
        setSelectedEmployee(employee);
        setIsDirty(false);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || t('employeeRecords.messages.updateFailed', 'Failed to update employee'));
      },
    }
  );

  const updateStatusMutation = useMutation(
    ({ id, status }: { id: string; status: EmployeeStatus }) => employeeApi.updateStatus(id, status),
    {
      onSuccess: () => {
        invalidateEmployeeQueries();
        toast.success(t('employeeRecords.messages.statusUpdated', 'Employee status updated successfully'));
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || t('employeeRecords.messages.statusFailed', 'Failed to update employee status'));
      },
    }
  );

  const deleteMutation = useMutation(employeeApi.delete, {
    onSuccess: () => {
      invalidateEmployeeQueries();
      toast.success(t('employeeRecords.messages.deleted', 'Employee deleted successfully'));
      setShowDeleteDialog(false);
      resetForm();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || t('employeeRecords.messages.deleteFailed', 'Failed to delete employee'));
    },
  });

  const filteredEmployees = useMemo(() => {
    return employees.filter((employee) => {
      const linkedUser = employee.linkedAppUserUsername || '';
      return (
        (!filters.firstName || employee.firstName.toLowerCase().includes(filters.firstName.toLowerCase())) &&
        (!filters.lastName || employee.lastName.toLowerCase().includes(filters.lastName.toLowerCase())) &&
        (!filters.employeeCode || (employee.employeeCode || '').toLowerCase().includes(filters.employeeCode.toLowerCase())) &&
        (!filters.email || (employee.email || '').toLowerCase().includes(filters.email.toLowerCase())) &&
        (!filters.phoneNumber || (employee.phoneNumber || '').toLowerCase().includes(filters.phoneNumber.toLowerCase())) &&
        (!filters.jobTitle || (employee.jobTitle || '').toLowerCase().includes(filters.jobTitle.toLowerCase())) &&
        (!filters.status || employee.status.toLowerCase().includes(filters.status.toLowerCase())) &&
        (!filters.linkedUser || linkedUser.toLowerCase().includes(filters.linkedUser.toLowerCase()))
      );
    });
  }, [employees, filters]);

  const paginatedEmployees = filteredEmployees.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);
  const totalPages = Math.max(1, Math.ceil(filteredEmployees.length / PAGE_SIZE));

  const handleStartCreate = () => {
    if (isDirty) {
      setPendingAction(() => () => {
        resetForm();
        setIsCreating(true);
      });
      setShowUnsavedDialog(true);
      return;
    }
    resetForm();
    setIsCreating(true);
  };

  const handleSelectEmployee = (employee: Employee) => {
    if (isDirty) {
      setPendingAction(() => () => handleSelectEmployeeInternal(employee));
      setShowUnsavedDialog(true);
      return;
    }
    handleSelectEmployeeInternal(employee);
  };

  const handleSelectEmployeeInternal = (employee: Employee) => {
    setSelectedEmployee(employee);
    setIsCreating(false);
    setIsDirty(false);
    setFormData({
      employeeCode: employee.employeeCode || '',
      firstName: employee.firstName,
      lastName: employee.lastName,
      email: employee.email || '',
      phoneNumber: employee.phoneNumber || '',
      jobTitle: employee.jobTitle || '',
      notes: employee.notes || '',
      status: employee.status,
    });
  };

  const handleSave = () => {
    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      toast.error(t('validation.requiredEmployeeRecordFields', 'Please fill in the required employee fields.'));
      return;
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      toast.error(t('validation.email', 'Please enter a valid email address'));
      return;
    }

    const payload: EmployeeCreateRequest = {
      employeeCode: formData.employeeCode?.trim() || undefined,
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      email: formData.email?.trim() || undefined,
      phoneNumber: formData.phoneNumber?.trim() || undefined,
      jobTitle: formData.jobTitle?.trim() || undefined,
      notes: formData.notes?.trim() || undefined,
    };

    if (isCreating) {
      createMutation.mutate(payload);
      return;
    }

    if (!selectedEmployee) {
      return;
    }

    updateMutation.mutate({
      id: selectedEmployee.id.toString(),
      data: {
        ...payload,
        status: formData.status,
      },
    });
  };

  const handleDelete = () => {
    if (!selectedEmployee) {
      return;
    }
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = () => {
    if (!selectedEmployee) {
      return;
    }
    deleteMutation.mutate(selectedEmployee.id.toString());
  };

  const handleDiscardChanges = () => {
    setIsDirty(false);
    setShowUnsavedDialog(false);
    if (pendingAction) {
      pendingAction();
      setPendingAction(null);
    }
  };

  return (
    <Box>
      <Box
        sx={{
          position: 'sticky',
          top: 64,
          zIndex: 1100,
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}
      >
        <Container maxWidth="xl" disableGutters>
          <Toolbar sx={{ pl: { xs: 2, sm: 3 }, pr: { xs: 2, sm: 3 }, minHeight: '48px !important', py: 0.5 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleStartCreate}
                size="small"
                sx={{ textTransform: 'none' }}
              >
                {t('common.new')}
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                size="small"
                disabled={!isCreating && !selectedEmployee}
                sx={{ textTransform: 'none' }}
              >
                {t('common.save')}
              </Button>
              <Button
                variant="contained"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleDelete}
                size="small"
                disabled={!selectedEmployee}
                sx={{ textTransform: 'none' }}
              >
                {t('common.delete')}
              </Button>
            </Box>
          </Toolbar>
        </Container>
      </Box>

      <Container maxWidth="xl" disableGutters sx={{ py: 1 }}>
        <Grid container spacing={0}>
          <Grid item xs={12}>
            <Paper elevation={1}>
              <Toolbar
                sx={{
                  pl: { xs: 2, sm: 3 },
                  pr: { xs: 2, sm: 3 },
                  minHeight: '48px !important',
                  py: 0.5,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<Refresh fontSize="small" />}
                    size="small"
                    onClick={invalidateEmployeeQueries}
                    sx={{ textTransform: 'none' }}
                  >
                    {t('common.refresh')}
                  </Button>
                  <Button variant="outlined" startIcon={<Print fontSize="small" />} size="small" sx={{ textTransform: 'none' }}>
                    {t('common.print')}
                  </Button>
                </Box>

                <Box sx={{ flexGrow: 1 }} />

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IconButton size="small" onClick={() => setPage((prev) => Math.max(0, prev - 1))} disabled={page === 0}>
                    <ArrowBack fontSize="small" />
                  </IconButton>
                  <Typography variant="body2" color="text.secondary">
                    {filteredEmployees.length === 0 ? '0' : page * PAGE_SIZE + 1}-{Math.min((page + 1) * PAGE_SIZE, filteredEmployees.length)} {t('common.of')} {filteredEmployees.length}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => setPage((prev) => Math.min(totalPages - 1, prev + 1))}
                    disabled={page >= totalPages - 1}
                  >
                    <ArrowForward fontSize="small" />
                  </IconButton>
                  <Button
                    variant="text"
                    size="small"
                    startIcon={<FilterListOff fontSize="small" />}
                    onClick={() => {
                      setFilters({
                        firstName: '',
                        lastName: '',
                        employeeCode: '',
                        email: '',
                        phoneNumber: '',
                        jobTitle: '',
                        status: '',
                        linkedUser: '',
                      });
                      setPage(0);
                    }}
                    sx={{ textTransform: 'none' }}
                  >
                    {t('common.clearFilters')}
                  </Button>
                </Box>
              </Toolbar>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableFilterCell placeholder={t('fields.firstName')} value={filters.firstName} onChange={(value) => setFilters((prev) => ({ ...prev, firstName: value }))} />
                      <TableFilterCell placeholder={t('fields.lastName')} value={filters.lastName} onChange={(value) => setFilters((prev) => ({ ...prev, lastName: value }))} />
                      <TableFilterCell placeholder={t('fields.employeeCode', 'Employee Code')} value={filters.employeeCode} onChange={(value) => setFilters((prev) => ({ ...prev, employeeCode: value }))} />
                      <TableFilterCell placeholder={t('fields.email')} value={filters.email} onChange={(value) => setFilters((prev) => ({ ...prev, email: value }))} />
                      <TableFilterCell placeholder={t('fields.phoneNumber')} value={filters.phoneNumber} onChange={(value) => setFilters((prev) => ({ ...prev, phoneNumber: value }))} />
                      <TableFilterCell placeholder={t('fields.position', 'Job Title')} value={filters.jobTitle} onChange={(value) => setFilters((prev) => ({ ...prev, jobTitle: value }))} />
                      <TableFilterCell placeholder={t('fields.status')} value={filters.status} onChange={(value) => setFilters((prev) => ({ ...prev, status: value }))} />
                      <TableFilterCell placeholder={t('users.title', 'User Account')} value={filters.linkedUser} onChange={(value) => setFilters((prev) => ({ ...prev, linkedUser: value }))} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {isLoading ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                          <CircularProgress size={24} />
                        </TableCell>
                      </TableRow>
                    ) : paginatedEmployees.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                          {t('management.emptyEmployeeRecords', 'No employees found')}
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedEmployees.map((employee) => (
                        <TableRow
                          key={employee.id}
                          hover
                          selected={selectedEmployee?.id === employee.id}
                          onClick={() => handleSelectEmployee(employee)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell>{employee.firstName}</TableCell>
                          <TableCell>{employee.lastName}</TableCell>
                          <TableCell>{employee.employeeCode || '-'}</TableCell>
                          <TableCell>{employee.email || '-'}</TableCell>
                          <TableCell>{employee.phoneNumber || '-'}</TableCell>
                          <TableCell>{employee.jobTitle || '-'}</TableCell>
                          <TableCell>
                            <Chip
                              label={statusLabel(t, employee.status)}
                              size="small"
                              color={getStatusColor(employee.status) as any}
                              onClick={(event) => {
                                event.stopPropagation();
                                updateStatusMutation.mutate({
                                  id: employee.id.toString(),
                                  status: employee.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE',
                                });
                              }}
                            />
                          </TableCell>
                          <TableCell>{employee.linkedAppUserUsername || '-'}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          {(isCreating || selectedEmployee) && (
            <Grid item xs={12}>
              <Paper elevation={1} sx={{ mt: 2, p: 2, ...DETAIL_FORM_UNIFORM_FIELDS_SX }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="h6">
                    {isCreating
                      ? t('employeeRecords.newEmployee', 'New Employee')
                      : selectedEmployee?.fullName || `${selectedEmployee?.firstName || ''} ${selectedEmployee?.lastName || ''}`.trim()}
                  </Typography>
                  {!isCreating && selectedEmployee && (
                    <Chip label={statusLabel(t, selectedEmployee.status)} size="small" color={getStatusColor(selectedEmployee.status) as any} />
                  )}
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={7}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label={t('fields.firstName')}
                          value={formData.firstName}
                          onChange={(event) => {
                            setFormData((prev) => ({ ...prev, firstName: event.target.value }));
                            setIsDirty(true);
                          }}
                          required
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label={t('fields.lastName')}
                          value={formData.lastName}
                          onChange={(event) => {
                            setFormData((prev) => ({ ...prev, lastName: event.target.value }));
                            setIsDirty(true);
                          }}
                          required
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label={t('fields.employeeCode', 'Employee Code')}
                          value={formData.employeeCode || ''}
                          onChange={(event) => {
                            setFormData((prev) => ({ ...prev, employeeCode: event.target.value }));
                            setIsDirty(true);
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small">
                          <InputLabel>{t('fields.status')}</InputLabel>
                          <Select
                            value={formData.status}
                            label={t('fields.status')}
                            onChange={(event) => {
                              setFormData((prev) => ({ ...prev, status: event.target.value as EmployeeStatus }));
                              setIsDirty(true);
                            }}
                            disabled={isCreating}
                          >
                            <MenuItem value="ACTIVE">{t('status.active')}</MenuItem>
                            <MenuItem value="INACTIVE">{t('status.inactive')}</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label={t('fields.email')}
                          type="email"
                          value={formData.email || ''}
                          onChange={(event) => {
                            setFormData((prev) => ({ ...prev, email: event.target.value }));
                            setIsDirty(true);
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label={t('fields.phoneNumber')}
                          value={formData.phoneNumber || ''}
                          onChange={(event) => {
                            setFormData((prev) => ({ ...prev, phoneNumber: event.target.value }));
                            setIsDirty(true);
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          size="small"
                          label={t('fields.position', 'Job Title')}
                          value={formData.jobTitle || ''}
                          onChange={(event) => {
                            setFormData((prev) => ({ ...prev, jobTitle: event.target.value }));
                            setIsDirty(true);
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          size="small"
                          multiline
                          minRows={4}
                          label={t('fields.notes')}
                          value={formData.notes || ''}
                          onChange={(event) => {
                            setFormData((prev) => ({ ...prev, notes: event.target.value }));
                            setIsDirty(true);
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item xs={12} md={5}>
                    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                      <Typography variant="subtitle1" gutterBottom>
                        {t('userAccounts.title', 'User Account')}
                      </Typography>
                      {selectedEmployee?.linkedAppUserUsername ? (
                        <>
                          <Typography variant="body2" color="text.secondary">
                            {t('employeeRecords.linkedUserHelp', 'This employee is already linked to an application user.')}
                          </Typography>
                          <Typography variant="body1" sx={{ mt: 1 }}>
                            {selectedEmployee.linkedAppUserUsername}
                          </Typography>
                        </>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {t('employeeRecords.unlinkedUserHelp', 'No application user is linked to this employee yet.')}
                        </Typography>
                      )}
                    </Paper>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          )}
        </Grid>
      </Container>

      <Dialog open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)}>
        <DialogTitle>{t('management.deleteEmployeeRecord', 'Delete Employee')}</DialogTitle>
        <DialogContent>
          <Typography>
            {selectedEmployee?.linkedAppUserId
              ? t('employeeRecords.deleteLinkedBlocked', 'This employee is linked to an application user. Unlink that account before deleting the employee.')
              : t('dialogs.deleteBody', {
                  item: selectedEmployee?.fullName || t('fields.employeeRecord', 'employee'),
                  defaultValue: `Are you sure you want to delete ${selectedEmployee?.fullName || 'this employee'}? This action cannot be undone.`,
                })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteDialog(false)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            disabled={deleteMutation.isLoading || !!selectedEmployee?.linkedAppUserId}
          >
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showUnsavedDialog} onClose={() => setShowUnsavedDialog(false)}>
        <DialogTitle>{t('dialogs.unsavedTitle')}</DialogTitle>
        <DialogContent>
          <Typography>{t('dialogs.unsavedBody')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowUnsavedDialog(false)}>{t('common.keepEditing')}</Button>
          <Button color="error" variant="contained" onClick={handleDiscardChanges}>
            {t('common.discardChanges')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
